/**
 * Marketing navigation — single source of truth for the public header
 * and footer. Every link users see on the marketing site lives here.
 *
 * Items are grouped so the header can render dropdown megamenus and the
 * footer can render its five-column sitemap from the same data. Paths
 * must match real routes registered in App.tsx.
 */

export interface NavItem {
  label: string; // i18n key fallback uses this string
  labelKey: string; // i18n key
  href: string;
  description?: string;
  descriptionKey?: string;
  badge?: string; // e.g. "New", "Beta"
}

export interface NavGroup {
  label: string;
  labelKey: string;
  items: NavItem[];
}

/** Header dropdowns — five columns of products, AI, solutions, resources, company. */
export const HEADER_MENUS: NavGroup[] = [
  {
    label: "Products",
    labelKey: "mkt_nav_products",
    items: [
      {
        label: "Composer",
        labelKey: "mkt_nav_composer",
        href: "/product/composer",
        description: "Multi-platform post creator",
        descriptionKey: "mkt_nav_composer_desc",
      },
      {
        label: "Scheduler & Queue",
        labelKey: "mkt_nav_scheduler",
        href: "/product/scheduler",
        description: "Calendar, queues, optimal times",
        descriptionKey: "mkt_nav_scheduler_desc",
      },
      {
        label: "Analytics",
        labelKey: "mkt_nav_analytics",
        href: "/product/analytics",
        description: "Cross-platform performance",
        descriptionKey: "mkt_nav_analytics_desc",
      },
      {
        label: "Link-in-Bio",
        labelKey: "mkt_nav_link_in_bio",
        href: "/product/link-in-bio",
        description: "Drag-and-drop landing pages",
        descriptionKey: "mkt_nav_link_in_bio_desc",
      },
      {
        label: "Unified Inbox",
        labelKey: "mkt_nav_inbox",
        href: "/product/inbox",
        description: "DMs & comments in one place",
        descriptionKey: "mkt_nav_inbox_desc",
      },
      {
        label: "Approvals",
        labelKey: "mkt_nav_approvals",
        href: "/product/approvals",
        description: "Multi-step review workflow",
        descriptionKey: "mkt_nav_approvals_desc",
      },
      {
        label: "Automations",
        labelKey: "mkt_nav_automations",
        href: "/product/automations",
        description: "Trigger → condition → action",
        descriptionKey: "mkt_nav_automations_desc",
      },
      {
        label: "Social Listening",
        labelKey: "mkt_nav_listening",
        href: "/product/listening",
        description: "Keywords, mentions, sentiment",
        descriptionKey: "mkt_nav_listening_desc",
      },
    ],
  },
  {
    label: "AI",
    labelKey: "mkt_nav_ai",
    items: [
      {
        label: "AI Studio",
        labelKey: "mkt_nav_ai_studio",
        href: "/ai/studio",
        description: "12-agent content orchestrator",
        descriptionKey: "mkt_nav_ai_studio_desc",
      },
      {
        label: "AI Images",
        labelKey: "mkt_nav_ai_images",
        href: "/ai/images",
        description: "DALL-E, Flux, SDXL, Recraft",
        descriptionKey: "mkt_nav_ai_images_desc",
      },
      {
        label: "AI Agent",
        labelKey: "mkt_nav_ai_agent",
        href: "/ai/agent",
        description: "Chat-driven post creation",
        descriptionKey: "mkt_nav_ai_agent_desc",
      },
      {
        label: "Repurpose Intelligence",
        labelKey: "mkt_nav_repurpose",
        href: "/ai/repurpose",
        description: "Recycle top content smartly",
        descriptionKey: "mkt_nav_repurpose_desc",
      },
    ],
  },
  {
    label: "Solutions",
    labelKey: "mkt_nav_solutions",
    items: [
      {
        label: "For Agencies",
        labelKey: "mkt_nav_sol_agencies",
        href: "/solutions/agencies",
        description: "Manage dozens of client accounts",
        descriptionKey: "mkt_nav_sol_agencies_desc",
      },
      {
        label: "For Creators",
        labelKey: "mkt_nav_sol_creators",
        href: "/solutions/creators",
        description: "Grow your audience across platforms",
        descriptionKey: "mkt_nav_sol_creators_desc",
      },
      {
        label: "For Small Business",
        labelKey: "mkt_nav_sol_smb",
        href: "/solutions/small-business",
        description: "Do more with a tiny team",
        descriptionKey: "mkt_nav_sol_smb_desc",
      },
      {
        label: "For Enterprise",
        labelKey: "mkt_nav_sol_enterprise",
        href: "/solutions/enterprise",
        description: "SSO, approvals, white-label",
        descriptionKey: "mkt_nav_sol_enterprise_desc",
      },
      {
        label: "For Ecommerce",
        labelKey: "mkt_nav_sol_ecommerce",
        href: "/solutions/ecommerce",
        description: "Product-led social commerce",
        descriptionKey: "mkt_nav_sol_ecommerce_desc",
      },
    ],
  },
  {
    label: "Resources",
    labelKey: "mkt_nav_resources",
    items: [
      {
        label: "Blog",
        labelKey: "mkt_nav_blog",
        href: "/resources/blog",
        description: "Growth, strategy, platform tips",
        descriptionKey: "mkt_nav_blog_desc",
      },
      {
        label: "FAQ",
        labelKey: "mkt_nav_faq",
        href: "/resources/faq",
        description: "Quick answers to common questions",
        descriptionKey: "mkt_nav_faq_desc",
      },
      {
        label: "Academy",
        labelKey: "mkt_nav_academy",
        href: "/resources/academy",
        description: "Courses and certifications",
        descriptionKey: "mkt_nav_academy_desc",
      },
      {
        label: "Changelog",
        labelKey: "mkt_nav_changelog",
        href: "/resources/changelog",
        description: "What we shipped this week",
        descriptionKey: "mkt_nav_changelog_desc",
      },
      {
        label: "Platform Guides",
        labelKey: "mkt_nav_platform_guides",
        href: "/platforms",
        description: "Per-platform playbooks",
        descriptionKey: "mkt_nav_platform_guides_desc",
      },
      {
        label: "Compare",
        labelKey: "mkt_nav_compare",
        href: "/compare",
        description: "PlugD vs the competition",
        descriptionKey: "mkt_nav_compare_desc",
      },
    ],
  },
];

/** Top-level links rendered as plain buttons (no dropdown). */
export const HEADER_FLAT_LINKS: NavItem[] = [
  { label: "Pricing", labelKey: "mkt_nav_pricing", href: "/pricing" },
];

/** Footer sitemap — five columns mirroring header structure. */
export const FOOTER_COLUMNS: NavGroup[] = [
  {
    label: "Product",
    labelKey: "mkt_footer_col_product",
    items: [
      { label: "Composer", labelKey: "mkt_nav_composer", href: "/product/composer" },
      { label: "Scheduler", labelKey: "mkt_nav_scheduler", href: "/product/scheduler" },
      { label: "Analytics", labelKey: "mkt_nav_analytics", href: "/product/analytics" },
      { label: "Link-in-Bio", labelKey: "mkt_nav_link_in_bio", href: "/product/link-in-bio" },
      { label: "Inbox", labelKey: "mkt_nav_inbox", href: "/product/inbox" },
      { label: "Approvals", labelKey: "mkt_nav_approvals", href: "/product/approvals" },
      { label: "Automations", labelKey: "mkt_nav_automations", href: "/product/automations" },
      { label: "Listening", labelKey: "mkt_nav_listening", href: "/product/listening" },
    ],
  },
  {
    label: "AI",
    labelKey: "mkt_footer_col_ai",
    items: [
      { label: "AI Studio", labelKey: "mkt_nav_ai_studio", href: "/ai/studio" },
      { label: "AI Images", labelKey: "mkt_nav_ai_images", href: "/ai/images" },
      { label: "AI Agent", labelKey: "mkt_nav_ai_agent", href: "/ai/agent" },
      { label: "Repurpose", labelKey: "mkt_nav_repurpose", href: "/ai/repurpose" },
    ],
  },
  {
    label: "Solutions",
    labelKey: "mkt_footer_col_solutions",
    items: [
      { label: "Agencies", labelKey: "mkt_nav_sol_agencies", href: "/solutions/agencies" },
      { label: "Creators", labelKey: "mkt_nav_sol_creators", href: "/solutions/creators" },
      { label: "Small Business", labelKey: "mkt_nav_sol_smb", href: "/solutions/small-business" },
      { label: "Enterprise", labelKey: "mkt_nav_sol_enterprise", href: "/solutions/enterprise" },
      { label: "Ecommerce", labelKey: "mkt_nav_sol_ecommerce", href: "/solutions/ecommerce" },
    ],
  },
  {
    label: "Resources",
    labelKey: "mkt_footer_col_resources",
    items: [
      { label: "Pricing", labelKey: "mkt_nav_pricing", href: "/pricing" },
      { label: "Blog", labelKey: "mkt_nav_blog", href: "/resources/blog" },
      { label: "FAQ", labelKey: "mkt_nav_faq", href: "/resources/faq" },
      { label: "Academy", labelKey: "mkt_nav_academy", href: "/resources/academy" },
      { label: "Changelog", labelKey: "mkt_nav_changelog", href: "/resources/changelog" },
      { label: "Platform Guides", labelKey: "mkt_nav_platform_guides", href: "/platforms" },
      { label: "Compare", labelKey: "mkt_nav_compare", href: "/compare" },
    ],
  },
  {
    label: "Company",
    labelKey: "mkt_footer_col_company",
    items: [
      { label: "About", labelKey: "mkt_nav_about", href: "/company/about" },
      { label: "Contact", labelKey: "mkt_nav_contact", href: "/company/contact" },
      { label: "Careers", labelKey: "mkt_nav_careers", href: "/company/careers" },
      { label: "Security", labelKey: "mkt_nav_security", href: "/company/security" },
      { label: "Terms", labelKey: "mkt_nav_terms", href: "/terms" },
      { label: "Privacy", labelKey: "mkt_nav_privacy", href: "/privacy" },
    ],
  },
];

/**
 * Supported platforms — used by PlatformGrid, PlatformPage, and footer.
 * Each slug maps to a real OAuth integration in the backend.
 */
export interface PlatformMeta {
  slug: string;
  name: string;
  tagline: string;
  taglineKey: string;
  aspectNote: string; // e.g. "Feed 4:5, Story 9:16"
  charLimit: number;
  hashtagLimit: number;
  color: string; // hex for accent
}

export const PLATFORMS: PlatformMeta[] = [
  {
    slug: "instagram",
    name: "Instagram",
    tagline: "Reels, carousels, stories — auto-formatted.",
    taglineKey: "mkt_platform_instagram_tagline",
    aspectNote: "Feed 4:5, Story 9:16",
    charLimit: 2200,
    hashtagLimit: 30,
    color: "#E4405F",
  },
  {
    slug: "facebook",
    name: "Facebook",
    tagline: "Pages, groups, and multi-image posts.",
    taglineKey: "mkt_platform_facebook_tagline",
    aspectNote: "Feed 1:1 or 4:5",
    charLimit: 63206,
    hashtagLimit: 30,
    color: "#1877F2",
  },
  {
    slug: "twitter",
    name: "X (Twitter)",
    tagline: "Threads, polls, and reply chains.",
    taglineKey: "mkt_platform_twitter_tagline",
    aspectNote: "Any ratio, 16:9 preview",
    charLimit: 280,
    hashtagLimit: 3,
    color: "#000000",
  },
  {
    slug: "linkedin",
    name: "LinkedIn",
    tagline: "Professional posts, carousels, articles.",
    taglineKey: "mkt_platform_linkedin_tagline",
    aspectNote: "Feed 1.91:1 or 16:9",
    charLimit: 3000,
    hashtagLimit: 5,
    color: "#0A66C2",
  },
  {
    slug: "tiktok",
    name: "TikTok",
    tagline: "Short-form video with platform-native specs.",
    taglineKey: "mkt_platform_tiktok_tagline",
    aspectNote: "Video 9:16",
    charLimit: 2200,
    hashtagLimit: 5,
    color: "#000000",
  },
  {
    slug: "youtube",
    name: "YouTube",
    tagline: "Shorts and long-form uploads.",
    taglineKey: "mkt_platform_youtube_tagline",
    aspectNote: "Shorts 9:16, Long 16:9",
    charLimit: 5000,
    hashtagLimit: 15,
    color: "#FF0000",
  },
  {
    slug: "pinterest",
    name: "Pinterest",
    tagline: "Rich pins with product and article tags.",
    taglineKey: "mkt_platform_pinterest_tagline",
    aspectNote: "Pin 2:3 or 4:5",
    charLimit: 500,
    hashtagLimit: 20,
    color: "#E60023",
  },
  {
    slug: "bluesky",
    name: "Bluesky",
    tagline: "AT Protocol posts with native threading.",
    taglineKey: "mkt_platform_bluesky_tagline",
    aspectNote: "Any ratio",
    charLimit: 300,
    hashtagLimit: 5,
    color: "#0085FF",
  },
  {
    slug: "threads",
    name: "Threads",
    tagline: "Conversation-native posts from Meta.",
    taglineKey: "mkt_platform_threads_tagline",
    aspectNote: "Any ratio",
    charLimit: 500,
    hashtagLimit: 10,
    color: "#000000",
  },
];

/** Competitors covered by /compare/:slug pages. */
export interface CompetitorMeta {
  slug: string;
  name: string;
  tagline: string;
  taglineKey: string;
}

export const COMPETITORS: CompetitorMeta[] = [
  {
    slug: "hootsuite",
    name: "Hootsuite",
    tagline: "PlugD vs Hootsuite — modern AI, lower price.",
    taglineKey: "mkt_compare_hootsuite_tagline",
  },
  {
    slug: "buffer",
    name: "Buffer",
    tagline: "PlugD vs Buffer — add AI, automations, link-in-bio.",
    taglineKey: "mkt_compare_buffer_tagline",
  },
  {
    slug: "later",
    name: "Later",
    tagline: "PlugD vs Later — beyond Instagram-first scheduling.",
    taglineKey: "mkt_compare_later_tagline",
  },
  {
    slug: "sprout-social",
    name: "Sprout Social",
    tagline: "PlugD vs Sprout Social — enterprise power, startup price.",
    taglineKey: "mkt_compare_sprout_tagline",
  },
  {
    slug: "onlypult",
    name: "Onlypult",
    tagline: "PlugD vs Onlypult — deeper AI, automations, and approvals.",
    taglineKey: "mkt_compare_onlypult_tagline",
  },
];
